import React, { useState, useCallback } from "react";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useNavigate } from "react-router-dom";
import "../styles/doubleslider.css";
import backImage from "../assets/back.png";
import logo from "../assets/logo.png";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import axios from "axios";

export default function DoubleSlider() {
  const [isSignUpActive, setIsSignUpActive] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);

  const [signUpForm, setSignUpForm] = useState({ name: "", email: "", password: "" });
  const [signUpMessage, setSignUpMessage] = useState("");

  const [signInForm, setSignInForm] = useState({ email: "", password: "" });
  const [signInMessage, setSignInMessage] = useState("");

  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotStep, setForgotStep] = useState(1); // 1 = send email, 2 = reset password
  const [signUpLoading, setSignUpLoading] = useState(false);
  // For Sign Up verification flow
  const [signUpStep, setSignUpStep] = useState(1); // 1 = enter info, 2 = enter code
  const [emailCode, setEmailCode] = useState("");
  const [showAdminModal, setShowAdminModal] = useState(false);

  const navigate = useNavigate();

  const toggleActive = useCallback(() => {
    setIsSignUpActive((prev) => !prev);
    setSignUpMessage("");
    setSignInMessage("");
  }, []);

  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  // --- Sign Up ---
  const handleSignUpChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === "name") {
      // Remove numbers and special characters, allow letters and spaces only
      const lettersOnly = value.replace(/[^A-Za-z\s]/g, "");
      setSignUpForm((prev) => ({ ...prev, [name]: lettersOnly }));
    } else {
      setSignUpForm((prev) => ({ ...prev, [name]: value }));
    }
  }, []);
const handleSendSignUpCodeWithValidation = async (e) => {
  e.preventDefault();
  setSignUpMessage("");

  // ✅ Add password length validation
  if (signUpForm.password.length < 6) {
    setSignUpMessage("❌ Password must be at least 6 characters long.");
    return;
  }

  setSignUpLoading(true);

  try {
    const res = await axios.post("https://firetrace-capstone.onrender.com/api/auth/send-verification-code", {
      email: signUpForm.email,
    });

    if (res.data.success) {
      setSignUpMessage("✅ Verification code sent to your email.");
      setSignUpStep(2);
    } else {
      setSignUpMessage(`❌ ${res.data.error || "Failed to send code."}`);
    }
  } catch {
    setSignUpMessage("❌ Network error.");
  } finally {
    setSignUpLoading(false);
  }
};

  const handleSignUp = useCallback(
    async (e) => {
      e.preventDefault();
      setSignUpMessage("");
      setSignUpLoading(true); // ✅ start loading

      try {
        const response = await fetch("https://firetrace-capstone.onrender.com/api/auth/signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(signUpForm),
        });
        const data = await response.json();

        if (response.ok) {
          setSignUpMessage("✅ Signup successful! You can now sign in.");
          setSignUpForm({ name: "", email: "", password: "" });
          setIsSignUpActive(false);
        } else {
          setSignUpMessage(`❌ ${data.error || "Signup failed."}`);
        }
      } catch {
        setSignUpMessage("❌ Network error. Try again.");
      } finally {
        setSignUpLoading(false); // ✅ stop loading
      }
    },
    [signUpForm]
  );

  // --- Sign In ---
  const handleSignInChange = useCallback((e) => {
    const { name, value } = e.target;
    setSignInForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSignIn = useCallback(
    async (e) => {
      e.preventDefault();
      setSignInMessage("");
      setIsSigningIn(true);

      try {
        const response = await fetch("https://firetrace-capstone.onrender.com/api/auth/signin", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(signInForm),
        });

        const data = await response.json();

        if (response.ok) {
          // If admin tries to login, show message instead of signing in
          if (data.user && data.user.type === "admin") {
            setSignInMessage("❌ Please login on a desktop.");
            setIsSigningIn(false);
            return;
          }

          setSignInMessage("✅ Sign in successful!");
          setTempUserData(data);
          setShowPrivacyPopup(true);
        } else {
          setSignInMessage(`❌ ${data.error || "Sign in failed."}`);
        }
      } catch (error) {
        setSignInMessage("❌ Network error. Try again.");
      } finally {
        setIsSigningIn(false);
      }
    },
    [signInForm]
  );

  // Step 1: send verification code
  const handleSendSignUpCode = async (e) => {
    e.preventDefault();
    setSignUpMessage("");
    setSignUpLoading(true);

    try {
      const res = await axios.post("https://firetrace-capstone.onrender.com/api/auth/send-verification-code", {
        email: signUpForm.email,
      });

      if (res.data.success) {
        setSignUpMessage("✅ Verification code sent to your email.");
        setSignUpStep(2);
      } else {
        setSignUpMessage(`❌ ${res.data.error || "Failed to send code."}`);
      }
    } catch {
      setSignUpMessage("❌ Network error.");
    } finally {
      setSignUpLoading(false);
    }
  };

  // Step 2: verify code and create account
  const handleVerifySignUpCode = async () => {
    setSignUpMessage("");
    setSignUpLoading(true);

    try {
      const res = await axios.post("https://firetrace-capstone.onrender.com/api/auth/verify-email-code", {
        email: signUpForm.email,
        code: emailCode,
        name: signUpForm.name,
        password: signUpForm.password,
      });

      if (res.data.success) {
        setSignUpMessage("✅ Signup successful! You can now sign in.");
        setSignUpForm({ name: "", email: "", password: "" });
        setEmailCode("");
        setSignUpStep(1);
        setIsSignUpActive(false);
      } else {
        setSignUpMessage(`❌ ${res.data.error || "Invalid code."}`);
      }
    } catch {
      setSignUpMessage("❌ Network error.");
    } finally {
      setSignUpLoading(false);
    }
  };
const handleResetPasswordVer = async (e) => {
  e.preventDefault();
  setForgotMessage("");

  // Check new password length
  if (newPassword.length < 6) {
    setForgotMessage("❌ Password must be at least 6 characters long.");
    return;
  }

  try {
    const res = await axios.post("https://firetrace-capstone.onrender.com/api/auth/reset-password", {
      email: forgotEmail,
      code: verificationCode,
      newPassword,
    });

    if (res.data.success) {
      setForgotMessage("✅ Password reset successful!");
      setTimeout(() => setShowForgot(false), 2000);
    } else {
      setForgotMessage(`❌ ${res.data.error || "Failed to reset password."}`);
    }
  } catch {
    setForgotMessage("❌ Network error.");
  }
};

  // --- Privacy Agreement ---
  const handleAgree = () => {
    if (tempUserData) {
      const userWithType = tempUserData.user
        ? { ...tempUserData.user, type: tempUserData.user.type || "user" }
        : { email: signInForm.email, type: "user" };

      localStorage.setItem("user", JSON.stringify(userWithType));
      localStorage.setItem("userId", userWithType._id || "");
      localStorage.setItem("userType", userWithType.type);

      if (tempUserData.token) {
        localStorage.setItem("token", tempUserData.token);
      }
    }
    setSignInForm({ email: "", password: "" });
    setShowPrivacyPopup(false);
    navigate("/dashboard", { replace: true });
  };

  const handleDisagree = () => {
    setShowPrivacyPopup(false);
    setTempUserData(null);
    setSignInMessage("❌ You must agree to the privacy policy to continue.");
  };

  // --- Forgot Password ---
  const handleSendCode = async (e) => {
    e.preventDefault();
    setForgotMessage("");
    try {
      const res = await axios.post("https://firetrace-capstone.onrender.com/api/auth/forgot-password", {
        email: forgotEmail,
      });
      if (res.data.success) {
        setForgotMessage("✅ Verification code sent to your email.");
        setForgotStep(2);
      } else {
        setForgotMessage(`❌ ${res.data.error || "Failed to send code."}`);
      }
    } catch {
      setForgotMessage("❌ Network error.");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotMessage("");
    try {
      const res = await axios.post("https://firetrace-capstone.onrender.com/api/auth/reset-password", {
        email: forgotEmail,
        code: verificationCode,
        newPassword,
      });
      if (res.data.success) {
        setForgotMessage("✅ Password reset successful!");
        setTimeout(() => setShowForgot(false), 2000);
      } else {
        setForgotMessage(`❌ ${res.data.error || "Failed to reset password."}`);
      }
    } catch {
      setForgotMessage("❌ Network error.");
    }
  };

  return (
    <div className="auth-wrapper">
      {/* 🔥 Page Dark Overlay When Loading */}
      {(isSigningIn || signUpLoading) && (
        <div className="loading-overlay">
          <div className="spinner large"></div>
        </div>
      )}

      <div className="auth-card" style={{ position: "relative", zIndex: 1 }}>
        <div className="logo-container">
          <img src={logo} alt="FireTrace Logo" className="logo" />
        </div>

        {/* Toggle between Sign In / Sign Up */}
        {!isSignUpActive ? (
          <form onSubmit={handleSignIn} noValidate>
            <h1>Sign In</h1>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={signInForm.email}
              onChange={handleSignInChange}
              required
            />
            <div className="password-wrapper">
              <input
                type={showSignInPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={signInForm.password}
                onChange={handleSignInChange}
                required
                minLength={6}
                title="Password must be at least 6 characters long"
                style={{ paddingRight: "40px" }}
              />
              <span
                className="eye-icon1"
                onClick={() => setShowSignInPassword((prev) => !prev)}
              >
                {showSignInPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </span>
            </div>

            <p className="forgot-password" onClick={() => setShowForgot(true)}>
              Forgot Password?
            </p>

            <button type="submit" className="btn">
              {isSigningIn ? <div className="spinner" /> : "Sign In"}
            </button>

            {signInMessage && (
              <p className={`message ${signInMessage.startsWith("✅") ? "success" : "error"}`}>
                {signInMessage}
              </p>
            )}
          </form>
        ) : (
          <form onSubmit={handleSendSignUpCodeWithValidation} noValidate>
            <h1>Create Account</h1>
            <input
              type="text"
              name="name"
              placeholder="Full Name (First Name Last Name)"
              value={signUpForm.name}
              onChange={handleSignUpChange}
              required
              pattern="[A-Za-z\s]+"
              title="Name can only contain letters and spaces"
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={signUpForm.email}
              onChange={handleSignUpChange}
              required
            />
            <div className="password-wrapper">
              <input
                type={showSignUpPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={signUpForm.password}
                onChange={handleSignUpChange}
                required
                minLength={6}
                autoComplete="new-password"
                style={{ paddingRight: "40px" }}
                title="Password must be at least 6 characters long"
              />
              <button
                type="button"
                className="eye-icon"
                onClick={() => setShowSignUpPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showSignUpPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </button>
            </div>
            <button
              type="submit"
              className="btn"
              disabled={signUpLoading}
              style={{ position: "relative" }}
            >
              {signUpLoading ? <div className="spinner" /> : "Sign Up"}
            </button>
            {signUpMessage && (
              <p className={`message ${signUpMessage.startsWith("✅") ? "success" : "error"}`}>
                {signUpMessage}
              </p>
            )}
          </form>
        )}

        {/* Switch link */}
        <div className="toggle-links">
          <p>
            {isSignUpActive ? "Already have an account?" : "Don't have an account?"}
            <button
              type="button"
              className="link-btn"
              onClick={() => setIsSignUpActive((prev) => !prev)}
            >
              {isSignUpActive ? "Sign In" : "Sign Up"}
            </button>
          </p>
        </div>
      </div>

      {/* Privacy Popup */}
      {showPrivacyPopup && (
        <div className="popup-overlay1">
          <div className="popup1">
            <h2>Privacy Policy</h2>
            <p>
              By using FireTrace, you consent to the collection and processing of your images and
              data for fire hazard assessment. Your information will be kept secure and used only
              for this purpose.
            </p>
            <div className="popup-actions1">
              <button onClick={handleAgree} className="btn green">
                I Agree & Continue
              </button>
              <button onClick={handleDisagree} className="btn red">
                Disagree
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgot && (
        <div
          className="popup-overlay1"
          onClick={(e) => {
            if (e.target.classList.contains("popup-overlay1")) {
              setShowForgot(false);
              setForgotMessage("");
              setForgotStep(1);
            }
          }}
        >
          <div className="popup1" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              className="close-btn2"
              onClick={() => {
                setShowForgot(false);
                setForgotMessage("");
                setForgotStep(1);
              }}
              title="Close"
            >
              &times;
            </button>

            {forgotStep === 1 ? (
              <form onSubmit={handleSendCode}>
                <h2>Reset Password</h2>
                <input
                  type="email"
                  placeholder="Email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                />
                <button type="submit" className="btn">
                  Send Code
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetPasswordVer}>
                <h2>Reset Password</h2>
                <input
                  type="text"
                  placeholder="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                />
                <div className="password-wrapper">
                  <input
                    type={showForgotPassword ? "text" : "password"}
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                  <span
                    className="eye-icon1"
                    onClick={() => setShowForgotPassword((prev) => !prev)}
                  >
                    {showForgotPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                  </span>
                </div>
                <button type="submit" className="btn">
                  Reset Password
                </button>
              </form>
            )}

            {forgotMessage && (
              <p
                className={`message ${
                  forgotMessage.startsWith("✅") ? "success" : "error"
                }`}
              >
                {forgotMessage}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Email Verification Modal (Sign Up Step 2) */}
      {signUpStep === 2 && (
        <div
          className="popup-overlay1"
          onClick={(e) => {
            // close when clicking outside the popup
            if (e.target.classList.contains("popup-overlay1")) {
              setSignUpStep(1);
              setEmailCode("");
            }
          }}
        >
          <div className="popup1">
            <button
              className="close-btn2"
              onClick={() => {
                setSignUpStep(1);
                setEmailCode("");
              }}
              title="Close"
            >
              &times;
            </button>
            <h2>Enter Verification Code</h2>
            <input
              type="text"
              placeholder="Verification Code"
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value)}
              required
            />
            <button onClick={handleVerifySignUpCode} className="btn">
              Verify & Create Account
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
